import { apiRequest } from '@/api/http';
import { cursorPage } from '@/api/schemas/common';
import {
  UserDetail,
  UserListItem,
  BulkUsersResult,
  SetSuspensionResult,
  SuspensionAppealsResponse,
  ResolveSuspensionAppealResult,
  ApplyUserSanctionResult,
  UserRiskScore,
  TierBenefits,
  type SetTierBenefitsBody,
  type ListUsersQuery,
  type BulkUsersBody,
  type ListSuspensionAppealsQuery,
  type ResolveSuspensionAppealBody,
  type ApplyUserSanctionBody,
} from '@/api/schemas/users';
import type { CursorPage } from '@/api/schemas/common';

const idemKey = () => crypto.randomUUID();

function toQuery(q: ListUsersQuery): string {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.role) p.set('role', q.role);
  if (q.kycStatus) p.set('kycStatus', q.kycStatus);
  if (q.disabled !== undefined) p.set('disabled', String(q.disabled));
  if (q.suspended !== undefined) p.set('suspended', String(q.suspended));
  if (q.limit) p.set('limit', String(q.limit));
  if (q.cursor) p.set('cursor', q.cursor);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function listUsers(
  query: ListUsersQuery,
): Promise<CursorPage<import('@/api/schemas/users').UserListItem>> {
  return apiRequest(`/admin/users${toQuery(query)}`, {
    schema: cursorPage(UserListItem),
  });
}
export function getUser(id: string) {
  return apiRequest<import('@/api/schemas/users').UserDetail>(`/admin/users/${id}`, {
    schema: UserDetail,
  });
}
export function banUser(id: string, reason: string) {
  return apiRequest<void>(`/admin/users/${id}/ban`, {
    method: 'POST',
    body: { reason },
    idempotencyKey: idemKey(),
  });
}
export function unbanUser(id: string) {
  return apiRequest<void>(`/admin/users/${id}/unban`, {
    method: 'POST',
    idempotencyKey: idemKey(),
  });
}
export function disableUser(id: string) {
  return apiRequest<{ id: string; disabledAt: string | null }>(`/admin/users/${id}/disable`, {
    method: 'PATCH',
    idempotencyKey: idemKey(),
  });
}
export function forceLogout(id: string) {
  return apiRequest<{ id: string; sessionsRevoked: number }>(`/admin/users/${id}/logout`, {
    method: 'POST',
    idempotencyKey: idemKey(),
  });
}
export function tierOverride(id: string, body: { module: string; level: number; reason: string }) {
  return apiRequest<void>(`/admin/users/${id}/tier-override`, {
    method: 'POST',
    body,
    idempotencyKey: idemKey(),
  });
}
// #84 — set/lift de la suspensión TEMPORAL de un usuario.
// `suspendedUntil`: fecha ISO futura para suspender, `null` para levantarla.
// Escritura destructiva → Idempotency-Key, igual que ban/unban/disable/logout.
// El backend valida que la fecha sea futura y responde {id, suspendedUntil}.
export function setUserSuspension(id: string, suspendedUntil: string | null, reason?: string) {
  return apiRequest<SetSuspensionResult>(`/admin/users/${id}/suspension`, {
    method: 'PATCH',
    // #430 — el motivo ya se audita en el backend.
    body: { suspendedUntil, reason },
    idempotencyKey: idemKey(),
    schema: SetSuspensionResult,
  });
}

// #84 — the suspension appeal queue, filterable by status. Resolving one goes
// through `resolveSuspensionAppeal` below.
export function listSuspensionAppeals(query: ListSuspensionAppealsQuery = {}) {
  const p = new URLSearchParams();
  if (query.status) p.set('status', query.status);
  if (query.limit) p.set('limit', String(query.limit));
  const s = p.toString();
  return apiRequest<SuspensionAppealsResponse>(
    `/admin/users/suspension-appeals${s ? `?${s}` : ''}`,
    { schema: SuspensionAppealsResponse },
  );
}

/**
 * #432 — resolve an appeal (PATCH /admin/users/suspension-appeals/:appealId).
 *
 * This is NOT the same lever as `setUserSuspension`. Lifting the suspension from
 * the user detail clears `suspendedUntil` and leaves `SuspensionAppeal.status`
 * untouched, so the "Abiertas" tab keeps showing appeals that were already dealt
 * with and no ACCEPTED/REJECTED verdict is ever recorded. Only this endpoint
 * writes the verdict — and ACCEPTED lifts the suspension in the same
 * transaction, so there is nothing left to do afterwards.
 *
 * Destructive write → Idempotency-Key, like the rest of the admin actions.
 */
export function resolveSuspensionAppeal(
  appealId: string,
  body: ResolveSuspensionAppealBody,
): Promise<ResolveSuspensionAppealResult> {
  return apiRequest(`/admin/users/suspension-appeals/${appealId}`, {
    method: 'PATCH',
    body,
    idempotencyKey: idemKey(),
    schema: ResolveSuspensionAppealResult,
  });
}

// #22 — acción masiva. Idempotency-Key-gated; devuelve {done, failed[]}.
export function bulkUsers(body: BulkUsersBody): Promise<BulkUsersResult> {
  return apiRequest(`/admin/users/bulk`, {
    method: 'POST',
    body,
    idempotencyKey: idemKey(),
    schema: BulkUsersResult,
  });
}

/**
 * ko-backend#817 — apply one rung of the sanction ladder
 * (POST /admin/users/:id/sanctions).
 *
 * Two idempotency layers, on purpose: the `Idempotency-Key` header folds a
 * dropped client retry into one sanction (the middleware replays the same
 * answer), while `sanctionKey` in the body is the domain discriminator the
 * service anchors on — a case id, a report id — and is only sent when the
 * caller has one. The body is `.strict()` on the backend, so the object built
 * here carries exactly the keys that were filled and nothing else.
 *
 * A replay of the same `(user, tier, sanctionKey)` is a 200 with
 * `applied:false` and no effects, not an error.
 */
export function applyUserSanction(
  id: string,
  body: ApplyUserSanctionBody,
  idempotencyKey: string,
): Promise<ApplyUserSanctionResult> {
  const wire: ApplyUserSanctionBody = { tier: body.tier, reason: body.reason };
  if (body.sanctionKey !== undefined) wire.sanctionKey = body.sanctionKey;
  // `ECONOMIC_PENALTY` debits KOins, so the key belongs to the attempt
  // (ko-admin#246).
  return apiRequest(`/admin/users/${id}/sanctions`, {
    method: 'POST',
    body: wire,
    idempotencyKey,
    schema: ApplyUserSanctionResult,
  });
}

/**
 * ko-backend#818 — the cross-module risk score (GET /admin/users/:id/risk).
 * A pure read (`scoreUser`, not `recalcUser`): it leaves no audit row, so the
 * page may fetch it on open. 404 `USER_NOT_FOUND`; 403 on MANAGE_USERS / MFA.
 */
export function getUserRisk(id: string): Promise<UserRiskScore> {
  return apiRequest(`/admin/users/${id}/risk`, { schema: UserRiskScore });
}

/**
 * ko-backend#1061 — make or unmake an ADMIN (`PATCH /admin/users/:id/role`).
 * SOLO_ADMIN, Idempotency-Key, and step-up MFA always: the 403
 * MFA_STEP_UP_REQUIRED is answered by the code dialog `apiRequest` opens.
 * Refusals by code: ADMIN_SELF_ACTION, LAST_ADMIN, USER_ROLE_UNCHANGED,
 * ROLE_PROMOTION_WITHOUT_PASSWORD.
 */
export function changeUserRole(
  id: string,
  body: { role: 'USER' | 'STAFF' | 'ADMIN'; reason: string },
): Promise<{ id: string; from: string; to: string; sessionsRevoked: boolean }> {
  return apiRequest(`/admin/users/${id}/role`, {
    method: 'PATCH',
    body,
    idempotencyKey: crypto.randomUUID(),
  });
}

/**
 * ko-backend#1128 (D10) — pause or restore the user's tier benefits
 * (POST /admin/users/:id/benefits, MANAGE_USERS). 200 with the benefits of
 * every module after the change. The reason is shown to the user next to a
 * pause and lands on the audit row either way.
 */
export function setUserBenefits(id: string, body: SetTierBenefitsBody): Promise<TierBenefits> {
  return apiRequest(`/admin/users/${id}/benefits`, {
    method: 'POST',
    body,
    idempotencyKey: idemKey(),
    schema: TierBenefits,
  });
}
